/**
 * Copying from Slack's message box puts no HTML on the clipboard — only plain
 * text and Slack's own rich text under "slack/texty", a Quill delta. Without
 * this, every format was lost (product QA E4). A sent message copies real
 * HTML and goes the usual way.
 */

type Attrs = Record<string, unknown>;
type Op = { insert?: unknown; attributes?: Attrs };

const escape = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

/** One text run with the formats the editor supports (code stays plain). */
const inline = (text: string, attrs: Attrs = {}): string => {
  let html = escape(text);
  if (attrs.strike) html = `<s>${html}</s>`;
  if (attrs.underline) html = `<u>${html}</u>`;
  if (attrs.italic) html = `<em>${html}</em>`;
  if (attrs.bold) html = `<strong>${html}</strong>`;
  if (typeof attrs.link === "string" && SAFE_HREF.test(attrs.link))
    html = `<a href="${escape(attrs.link).replace(/"/g, "&quot;")}">${html}</a>`;
  return html;
};

type Line = { html: string; list: "ul" | "ol" | null; indent: number };

/** Quill lines: a "\n" run ends a line and carries that line's attributes. */
const toLines = (ops: Op[]): Line[] => {
  const lines: Line[] = [];
  let current = "";
  for (const op of ops) {
    if (typeof op.insert !== "string") continue;
    op.insert.split("\n").forEach((part, i) => {
      if (i > 0) {
        const attrs = op.attributes ?? {};
        lines.push({
          html: current,
          list:
            attrs.list === "ordered"
              ? "ol"
              : attrs.list === "bullet"
                ? "ul"
                : null,
          indent: Number(attrs.indent) || 0,
        });
        current = "";
      }
      if (part) current += inline(part, op.attributes);
    });
  }
  if (current) lines.push({ html: current, list: null, indent: 0 });
  return lines;
};

export const slackTextyToHtml = (texty: string): string => {
  let ops: unknown;
  try {
    ops = JSON.parse(texty)?.ops;
  } catch {
    return "";
  }
  if (!Array.isArray(ops)) return "";

  let html = "";
  // Open lists, outermost first; each has an item open while it is.
  const open: Array<{ tag: "ul" | "ol" }> = [];
  const closeTo = (depth: number) => {
    while (open.length > depth) html += `</li></${open.pop()?.tag}>`;
  };
  for (const line of toLines(ops as Op[])) {
    if (!line.list) {
      closeTo(0);
      html += `<p>${line.html || "<br>"}</p>`;
      continue;
    }
    const depth = line.indent + 1;
    closeTo(depth);
    if (open.length === depth && open[depth - 1].tag !== line.list)
      closeTo(depth - 1);
    if (open.length === depth) html += "</li>";
    while (open.length < depth) {
      html += `<${line.list}>`;
      open.push({ tag: line.list });
    }
    html += `<li>${line.html}`;
  }
  closeTo(0);
  return html;
};
